import type {GatsbyBrowser} from 'gatsby'

/**
 * Paints "Neue Version wird geladen" and reloads, when it is there.
 *
 * The function is defined by the inline script gatsby-ssr.tsx writes into the
 * head of every /app document and of the worker's app shell, and by
 * onClientEntry in gatsby-browser.tsx for a document that arrived without it.
 * It is reached through the window rather than imported, because this file is
 * compiled on its own into dist/gatsby and an import out of it would move
 * every emitted path by one directory. See src/update-guard.ts.
 */
const updateNotice = (): ((reload?: boolean) => void) | undefined =>
  (window as unknown as {__taxiAppUpdateNotice?: (reload?: boolean) => void})
    .__taxiAppUpdateNotice

/**
 * Take a new deployment the moment one is ready.
 *
 * The app registers gatsby-plugin-offline, so an installed PWA runs from a
 * service worker's cache. A new service worker installs but then waits for
 * every client of the old one to go away, and on iOS a home-screen app is only
 * "gone" when it is swiped out of the app switcher. Anyone who just closes it
 * keeps running yesterday's bundle indefinitely, which is exactly what happened:
 * deployment after deployment landed and the phone kept reporting the errors
 * that had already been fixed, because it was still running the code that had
 * them.
 *
 * Reloading here activates the waiting worker at the first safe moment. It is
 * one reload per deployment, and it only ever fires when a new bundle is
 * genuinely ready to take over. The reload is not silent any more: the line
 * "Neue Version wird geladen" is painted first, so the second the document is
 * blank reads as an update and not as the app dying. See
 * okf/architecture/offline.md.
 */
const RELOAD_STAMP = 'taxi-app:update-reload'
const RELOAD_GAP_MS = 20000

/**
 * Whether this document is one the reload exists for.
 *
 * The stale shell is an `/app` problem: the service worker serves its
 * precached shell for those routes and an installed app can sit on
 * yesterday's bundle for days. A public page has no shell, it is fetched
 * from the network, and the new worker takes over at the next navigation on
 * its own. So a public page must never reload itself, and on 2026-09-08 it
 * did: `updateNotice` is written into `/app` documents only, so on the
 * homepage this handler fell through to an unguarded reload and any browser
 * carrying an older worker reloaded every few seconds. See
 * okf/architecture/offline.md.
 */
const reloadBelongsHere = (): boolean => {
  try {
    if (window.location.pathname.startsWith('/app')) return true

    const standalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      (window.navigator as unknown as {standalone?: boolean}).standalone ===
        true

    return standalone
  } catch {
    return false
  }
}

/** One reload per document per twenty seconds, whoever asks for it. */
const reloadOnce = (): void => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_STAMP)) || 0

    if (Date.now() - last < RELOAD_GAP_MS) return

    window.sessionStorage.setItem(RELOAD_STAMP, String(Date.now()))
  } catch {
    // No session storage, no way to remember: reloading blind is how a loop
    // starts, so the update waits for the next navigation instead.
    return
  }

  window.location.reload()
}

export const onServiceWorkerUpdateReady: GatsbyBrowser['onServiceWorkerUpdateReady'] =
  () => {
    const notice = updateNotice()
    if (notice) {
      notice(true)
      return
    }

    if (!reloadBelongsHere()) return

    reloadOnce()
  }
